import React, { useState, useEffect, useRef } from 'react';
import { APIProvider, Map, AdvancedMarker, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Search, MapPin, Loader2, Navigation } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Sri Lanka default center (Colombo)
const SRI_LANKA_CENTER = { lat: 6.9271, lng: 79.8612 };

// Component to handle place autocomplete
function PlaceAutocomplete({ onPlaceSelect }) {
  const [placeAutocomplete, setPlaceAutocomplete] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);
  const places = useMapsLibrary('places');

  useEffect(() => {
    if (!places || !inputRef.current) return;

    const options = {
      fields: ['geometry', 'name', 'formatted_address', 'address_components'],
      componentRestrictions: { country: 'lk' }, // Restrict to Sri Lanka
      types: ['geocode', 'establishment'],
    };

    setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options));
  }, [places]);

  useEffect(() => {
    if (!placeAutocomplete) return;

    placeAutocomplete.addListener('place_changed', () => {
      const place = placeAutocomplete.getPlace();
      if (place.geometry && place.geometry.location) {
        onPlaceSelect(place);
        setInputValue(place.formatted_address || '');
      }
    });
  }, [placeAutocomplete, onPlaceSelect]);

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        type="text"
        placeholder="Search for a location in Sri Lanka..."
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        className="pr-10"
      />
      <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
    </div>
  );
}

// Component to update map center
function MapHandler({ center, zoom }) {
  const map = useMap();

  useEffect(() => {
    if (center && map) {
      map.panTo(center);
      if (zoom) {
        map.setZoom(zoom);
      }
    }
  }, [center, zoom, map]);

  return null;
}

const GoogleLocationPicker = ({ value, onChange }) => {
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [mapCenter, setMapCenter] = useState(SRI_LANKA_CENTER);
  const [mapZoom, setMapZoom] = useState(11);
  const [address, setAddress] = useState('');
  const [isGeolocating, setIsGeolocating] = useState(false);
  const geocoderRef = useRef(null);

  // Initialize with value if provided
  useEffect(() => {
    if (value?.latitude && value?.longitude) {
      const position = { lat: value.latitude, lng: value.longitude };
      setSelectedPosition(position);
      setMapCenter(position);
      if (value.address) {
        setAddress(value.address);
      }
    }
  }, []);

  // Update parent component when position changes
  useEffect(() => {
    if (selectedPosition && onChange) {
      onChange({
        latitude: selectedPosition.lat,
        longitude: selectedPosition.lng,
        address: address || `${selectedPosition.lat.toFixed(6)}, ${selectedPosition.lng.toFixed(6)}`,
      });
    }
  }, [selectedPosition, address]);

  const handlePlaceSelect = (place) => {
    if (place.geometry && place.geometry.location) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      const newPosition = { lat, lng };
      
      setSelectedPosition(newPosition);
      setMapCenter(newPosition);
      setMapZoom(15);
      setAddress(place.formatted_address || '');
    }
  };

  const handleMapClick = async (event) => {
    if (event.detail && event.detail.latLng) {
      const lat = event.detail.latLng.lat;
      const lng = event.detail.latLng.lng;
      const newPosition = { lat, lng };
      
      setSelectedPosition(newPosition);

      // Reverse geocoding to get address
      if (window.google && window.google.maps && window.google.maps.Geocoder) {
        if (!geocoderRef.current) {
          geocoderRef.current = new window.google.maps.Geocoder();
        }

        try {
          const result = await geocoderRef.current.geocode({ location: newPosition });
          if (result.results && result.results[0]) {
            setAddress(result.results[0].formatted_address);
          }
        } catch (error) {
          console.error('Reverse geocoding error:', error);
          setAddress(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
        }
      }
    }
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setIsGeolocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const newPosition = { lat, lng };
        
        setSelectedPosition(newPosition);
        setMapCenter(newPosition);
        setMapZoom(15);

        // Get address for current location
        if (window.google && window.google.maps && window.google.maps.Geocoder) {
          if (!geocoderRef.current) {
            geocoderRef.current = new window.google.maps.Geocoder();
          }

          try {
            const result = await geocoderRef.current.geocode({ location: newPosition });
            if (result.results && result.results[0]) {
              setAddress(result.results[0].formatted_address);
            }
          } catch (error) {
            console.error('Reverse geocoding error:', error);
          }
        }

        setIsGeolocating(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        alert('Unable to get your location. Please search manually or click on the map.');
        setIsGeolocating(false);
      }
    );
  };

  return (
    <div className="space-y-4">
      {/* Search Section */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Search Location</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleGetCurrentLocation}
            disabled={isGeolocating}
          >
            {isGeolocating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Getting location...
              </>
            ) : (
              <>
                <Navigation className="h-4 w-4 mr-2" />
                Use My Location
              </>
            )}
          </Button>
        </div>
        
        <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
          <PlaceAutocomplete onPlaceSelect={handlePlaceSelect} />
        </APIProvider>
      </div>

      {/* Selected Address Display */}
      {address && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-900">
            <MapPin className="inline h-4 w-4 mr-1" />
            <span className="font-medium">Selected Address:</span>
          </p>
          <p className="text-sm text-blue-700 mt-1">{address}</p>
        </div>
      )}

      {/* Map Section */}
      <div className="space-y-2">
        <Label>Click on the map to pin your exact location</Label>
        <div className="h-96 border-2 border-gray-300 rounded-lg overflow-hidden shadow-md">
          <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
            <Map
              defaultCenter={mapCenter}
              defaultZoom={mapZoom}
              mapId="zeilian-map"
              onClick={handleMapClick}
              gestureHandling="greedy"
              disableDefaultUI={false}
            >
              {selectedPosition && (
                <AdvancedMarker position={selectedPosition}>
                  <div className="bg-red-500 w-6 h-6 rounded-full border-4 border-white shadow-lg flex items-center justify-center">
                    <MapPin className="h-4 w-4 text-white" />
                  </div>
                </AdvancedMarker>
              )}
              <MapHandler center={mapCenter} zoom={mapZoom} />
            </Map>
          </APIProvider>
        </div>
      </div>

      {/* Coordinates Display */}
      {selectedPosition && (
        <div className="grid grid-cols-2 gap-4 p-3 bg-gray-50 rounded-md">
          <div>
            <Label className="text-xs text-gray-600">Latitude</Label>
            <p className="text-sm font-mono font-semibold">{selectedPosition.lat.toFixed(6)}</p>
          </div>
          <div>
            <Label className="text-xs text-gray-600">Longitude</Label>
            <p className="text-sm font-mono font-semibold">{selectedPosition.lng.toFixed(6)}</p>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-500 flex items-center gap-1">
        <MapPin className="h-3 w-3" />
        Tip: Search for a location, use your current location, or click directly on the map
      </p>
    </div>
  );
};

export default GoogleLocationPicker;
